import {Component, inject, signal, model, OnInit, DestroyRef, computed} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    FormsModule,
    Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

// Angular Material
import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import {NotificationModule} from "../../shared/notification/notification.module";
import {DragDropModule} from "../../drag-drop/drag-drop.module";
import {MatDialog} from "@angular/material/dialog";
import {MarketingService} from "../../../core/services/marketing.service";
import {AdsLocationsPipe} from "../../../core/pipes/adsLocations.pipe";
import {MatTableModule} from "@angular/material/table";
import {MatPaginatorModule, PageEvent} from "@angular/material/paginator";
import {MatSortModule, Sort} from "@angular/material/sort";
import {MatTooltipModule} from "@angular/material/tooltip";
import {TableSkeletonComponent} from "../../shared/skeletons/table-skeleton.component";
import {DialogsService} from "../../../core/services/cms/dialogs.service";
import {ConfirmationDialogModel} from "../../shared/confirmation-dialog/confirmation-dialog.model";
import {ConfirmationDialogComponent} from "../../shared/confirmation-dialog/confirmation-dialog.component";
import {PreviewImageDialog} from "../ads-management-2026/ads-management-2026.component";
import {PopupFormComponent} from "./popup-form/popup-form.component";

@Component({
    selector: 'phx-popup-management',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatStepperModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatButtonToggleModule,
        MatIconModule,
        MatButtonModule,
        NotificationModule,
        DragDropModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatTooltipModule,
        TableSkeletonComponent,
    ],
    templateUrl: './popup-management.component.html',
    styleUrl: './popup-management.component.scss',
})
export class PopupManagementComponent implements OnInit {
    protected dialog = inject(MatDialog);
    protected dialogsService = inject(DialogsService);
    protected marketingService = inject(MarketingService);
    protected destroyRef = inject(DestroyRef);

    size = signal(20);
    page = signal(0);
    filter = signal('');
    filterStatus = signal<string>('');
    company = signal('');
    location = signal('');
    status = signal('ALL');

    readonly locations = this.dialogsService.locations;

    sorting = signal({field: 'title', direction: 'desc' as 'asc' | 'desc'});

    displayedColumnsSlides: string[] = ['status', 'title', 'date', 'portal', 'actions'];

    tableData = computed(() => {
        const response = this.dialogsService.dialogs();
        const dialogs: any = response?.content || [];
        const statusFilter = this.filterStatus();


        return dialogs.map((dialog: any) => {
            const now = new Date();
            const start = new Date(dialog.start_date);
            const end = new Date(dialog.end_date);
            end.setHours(23, 59, 59, 999);

            let status = 'unknown';
            if (now > start && now < end) {
                status = 'active';
            } else if (now < start) {
                status = 'pending';
            } else if (now > end) {
                status = 'finished';
            }

            return {
                id: dialog.id || 0,
                // location: dialog.location || 'Error',
                title: dialog.title || '',
                // link: dialog.link || '',
                // imagePath: dialog.image || '',
                onHageda: dialog.company?.indexOf('HAGEDA') > -1 || false,
                onPhoenix: dialog.company?.indexOf('PHOENIX') > -1 || false,
                start: dialog.start_date ? new Date(dialog.start_date).toLocaleDateString('de-DE') : '',
                end: dialog.end_date ? new Date(dialog.end_date).toLocaleDateString('de-DE') : '',
                status: status
            };
        }).filter((dialog: any) => {
            if (!statusFilter) {
                return true;
            }
            return dialog.status === statusFilter;
        });
    });

    totalElements = computed(() => {
        const res = this.dialogsService.dialogs();
        return res?.total_elements || res?.totalElements || 0;
    });

    inputFilter = signal('');
    private debounceTimer: any;


    ngOnInit() {
        this.loadDialogs();
    }

    protected handlePageEvent(event: PageEvent) {
        this.page.set(event.pageIndex);
        this.size.set(event.pageSize);
        this.loadDialogs();
    }

    protected handleSortChange(sortEvent: Sort) {
        this.page.set(0);
        this.sorting.set({
            field: sortEvent.active,
            direction: sortEvent.direction as 'asc' | 'desc'
        });
        this.loadDialogs();
    }

    private loadDialogs() {
        this.dialogsService.getDialogs(
            this.page(),
            this.size(),
            this.filter(),
            this.sorting(),
            this.company(),
            this.location(),
            this.status()
        );
    }

    protected showImagePreview(imagePath: string) {
        this.dialog.open(PreviewImageDialog, {
            data: {imagePath}
        });
    }

    protected createDialog() {
        const dialogRef = this.dialog.open(PopupFormComponent, {
            width: '1000px',
            data: {mode: 'create'}
        });

        dialogRef.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                if (result && result.imageFile) {
                    this.marketingService.uploadImage(result.imageFile, 'dialogs/' + result.imageFile.name)
                        .then(() => {
                            result.data.image = 'dialogs/' + result.imageFile.name;
                            this.dialogsService.saveDialog(result.data)
                                .pipe(takeUntilDestroyed(this.destroyRef))
                                .subscribe({
                                    next: () => {
                                        this.loadDialogs();
                                        document.getElementById('NotificationContainer')?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'start',
                                            inline: 'nearest'
                                        });
                                    },
                                    error: (err) => {
                                        console.error('❌ Error creating dialog:', err);
                                    }
                                });
                        })
                        .catch(err => {
                            console.error('❌ Error uploading image:', err);
                        });
                }
            });
    }

    protected editDialog(element: any) {
        const originalDialog = this.dialogsService.dialogs()?.content?.find((dialog: any) => dialog.id === element.id);

        const dialogRef = this.dialog.open(PopupFormComponent, {
            width: '1000px',
            data: {mode: 'edit', dialog: originalDialog}
        });

        dialogRef.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                if (result) {
                    const saveAndLoad = () => {
                        this.dialogsService.saveDialog(result.data)
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe({
                                next: () => {
                                    this.loadDialogs();
                                    document.getElementById('NotificationContainer')?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start',
                                        inline: 'nearest'
                                    });
                                },
                                error: (err) => {
                                    console.error('❌ Error creating dialog:', err);
                                }
                            });
                    };

                    if (result.imageFile) {
                        this.marketingService.uploadImage(result.imageFile, 'dialogs/' + result.imageFile.name)
                            .then(() => {
                                result.data.image = 'dialogs/' + result.imageFile.name;
                                saveAndLoad();
                            });
                    } else {
                        saveAndLoad();
                    }
                }
            });
    }

    protected deleteDialog(element: any) {
        const dialogId = element['id'];
        const dialogData = new ConfirmationDialogModel('Dialog entfernen', 'Möchten Sie Dialog wirklich löschen?');
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '400px',
            closeOnNavigation: true,
            data: dialogData
        });

        dialogRef.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dialogResult => {
                if (dialogResult) {
                    this.dialogsService.deleteDialog(dialogId)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: () => {
                                this.loadDialogs();
                                document.getElementById('NotificationContainer')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                    inline: 'nearest'
                                });
                            }
                        });
                }
            });
    }


    protected onInputChange(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.inputFilter.set(value);
    }

    protected triggerSearch() {
        this.filter.set(this.inputFilter());
        this.page.set(0);
        this.loadDialogs();
    }

    protected clearFilter() {
        this.inputFilter.set('');
        this.filter.set('');
        this.page.set(0);
        this.loadDialogs();
    }

    protected onStatusChange(value: string) {
        this.status.set(value);
        this.page.set(0);
        this.loadDialogs();
    }

    protected onCompanyChange(value: any) {
        this.company.set(value);
        this.page.set(0);
        this.loadDialogs();
    }

    protected onLocationChange(value: any) {
        this.location.set(value);
        this.page.set(0);
        this.loadDialogs();
    }

    protected resetFilter() {
        this.inputFilter.set('');
        this.filter.set('');
        this.status.set('ALL');
        this.company.set('');
        this.location.set('');
        this.page.set(0);
        this.loadDialogs();
    }

}
